// Immutable tree helpers for lists/folders navigation tree

export const removeNode = (data, id, type) =>
  data
    .filter((item) => !(item.id === id && item.type === type))
    .map((item) => {
      if (Array.isArray(item.children) && item.children.length > 0) {
        return { ...item, children: removeNode(item.children, id, type) };
      }
      return item;
    });

export const insertChildNode = (data, targetId, targetType, newItem) =>
  data.map((item) => {
    if (item.id === targetId && item.type === targetType) {
      const children = Array.isArray(item.children) ? item.children : [];
      return { ...item, children: [newItem, ...children] };
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      return { ...item, children: insertChildNode(item.children, targetId, targetType, newItem) };
    }
    return item;
  });

export const findNode = (data, itemId, itemType) => {
  // Depth-first search without for...of to satisfy lint rules
  let found = null;
  data.some((item) => {
    const isMatch = itemType == null ? item.id === itemId : item.id === itemId && item.type === itemType;
    if (isMatch) {
      found = item;
      return true;
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      const result = findNode(item.children, itemId, itemType);
      if (result) {
        found = result;
        return true;
      }
    }
    return false;
  });
  return found;
};

export const insertBeforeNode = (data, targetId, targetType, newItem) =>
  data.flatMap((item) => {
    if (item.id === targetId && item.type === targetType) {
      return [newItem, item];
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      return { ...item, children: insertBeforeNode(item.children, targetId, targetType, newItem) };
    }
    return item;
  });

export const insertAfterNode = (data, targetId, targetType, newItem) =>
  data.flatMap((item) => {
    if (item.id === targetId && item.type === targetType) {
      return [item, newItem];
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      return { ...item, children: insertAfterNode(item.children, targetId, targetType, newItem) };
    }
    return item;
  });

export const updateNode = (data, nodeId, nodeType, getChanges) =>
  data.map((item) => {
    if (item.id === nodeId && item.type === nodeType) {
      return { ...item, ...getChanges(item) };
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      return { ...item, children: updateNode(item.children, nodeId, nodeType, getChanges) };
    }
    return item;
  });

// Utility helpers used by DnD logic
export const getChildrenOfParent = (tree, parentId) => {
  if (parentId == null) return tree;
  const parentNode = findNode(tree, parentId, 'folder');
  return Array.isArray(parentNode?.children) ? parentNode.children : [];
};

export const findIndexIn = (nodes, id, type) => nodes.findIndex((n) => n.id === id && n.type === type);

export const isReorderNoOp = (tree, originalParentId, destinationParentId, dragged, targetItem, inst) => {
  const siblings = getChildrenOfParent(tree, originalParentId);
  const oldIndex = findIndexIn(siblings, dragged.id, dragged.type);
  const targetSiblings = getChildrenOfParent(tree, destinationParentId);
  const targetIndex = findIndexIn(targetSiblings, targetItem.id, targetItem.type);

  return (
    originalParentId === destinationParentId &&
    ((inst.operation === 'reorder-before' && oldIndex === targetIndex - 1) ||
      (inst.operation === 'reorder-after' && oldIndex === targetIndex + 1))
  );
};

export const isCombineNoOp = (tree, originalParentId, destinationParentId, dragged) => {
  if (originalParentId !== destinationParentId) return false;
  const oldSiblings = getChildrenOfParent(tree, originalParentId);
  const oldIndex = findIndexIn(oldSiblings, dragged.id, dragged.type);
  // Combining inserts at the start; if already first, it's a no-op
  return oldIndex === 0;
};
